/**
 * InstantQuote — public/authed instant £-quote widget for the cost predictor.
 * Backend:
 *   POST /api/quote/instant
 *   GET  /api/quote/matrix
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Zap, Clock, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function InstantQuote() {
  const [matrix, setMatrix] = useState({ brands: [], faults: [], fault_labels: {} });
  const [brand, setBrand] = useState("Apple");
  const [category, setCategory] = useState("phone");
  const [fault, setFault] = useState("screen_crack");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/quote/matrix").then(r => {
      setMatrix(r.data);
      if (r.data.brands?.length) setBrand(r.data.brands[0]);
    }).catch(() => {});
  }, []);

  const getQuote = async () => {
    setLoading(true);
    setQuote(null);
    try {
      const r = await api.post("/quote/instant", { brand, category, fault });
      setQuote(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No quote available");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="instant-quote-page">
      <header className="max-w-4xl mx-auto px-6 pt-8">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <div className="font-display font-semibold text-lg">Treasurescare</div>
            <div className="text-[10px] overline text-muted-foreground">Instant repair quote</div>
          </div>
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center">
          <div className="overline text-secondary flex items-center justify-center gap-1"><Zap className="w-3 h-3" /> Sub-second quote engine</div>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-light tracking-tighter">
            Get an <span className="text-primary italic">instant</span> repair quote
          </h1>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            Real UK prices from our repair matrix. Members save 15% and skip the diagnostic wait.
          </p>
        </div>

        <Card className="mt-10 shadow-teal-glow">
          <CardHeader><CardTitle className="text-base">Pick your device & fault</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Brand</div>
                <Select value={brand} onValueChange={setBrand}>
                  <SelectTrigger data-testid="quote-brand"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(matrix.brands || []).map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Category</div>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="quote-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="tablet">Tablet</SelectItem>
                    <SelectItem value="wearable">Wearable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Fault</div>
                <Select value={fault} onValueChange={setFault}>
                  <SelectTrigger data-testid="quote-fault"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(matrix.faults || []).map(f => (
                      <SelectItem key={f} value={f}>{matrix.fault_labels?.[f] || f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button className="w-full" onClick={getQuote} disabled={loading} data-testid="quote-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Quote me <ArrowRight className="w-4 h-4 ml-1" /></>}
                </Button>
              </div>
            </div>

            {quote && (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="quote-result">
                <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
                  <div className="overline text-destructive">Standard price</div>
                  <div className="mt-2 font-display text-5xl font-semibold" data-testid="quote-price">£{quote.price_gbp}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{quote.fault_label}</div>
                </div>
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6">
                  <div className="overline text-primary">Member price</div>
                  <div className="mt-2 font-display text-5xl font-semibold text-primary" data-testid="quote-member-price">£{quote.member_price_gbp}</div>
                  <div className="mt-1 text-xs text-muted-foreground">Save 15% as a Treasurescare member</div>
                </div>
                <div className="md:col-span-2 flex flex-wrap gap-2 items-center">
                  <Badge variant="outline"><Clock className="w-3 h-3 mr-1" /> Typical {quote.typical_turnaround_hours}h turnaround</Badge>
                  <Badge variant="outline">Confidence · {quote.confidence_pct}%</Badge>
                  <Badge variant="outline">Valid {quote.quote_valid_days} days</Badge>
                </div>
                <div className="md:col-span-2 text-xs text-muted-foreground">{quote.disclaimer}</div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-10 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground underline">Back to Treasurescare</Link>
        </div>
      </main>
    </div>
  );
}
